import { Component, HostListener, OnInit } from '@angular/core';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

import { CustomCustomerField, OrganizationConfig } from '../../../../../../models/organization-config';

import { OrganizationConfigService } from '../../../../../../services/organization-config/organization-config.service';
import { scrollToElement } from '../../../../../../utils';
import { environment } from '../../../../../../../environments/environment';
import {
  getCustomerFieldLabel,
  getCustomerProfileActionButtonLabel,
  getCustomerProfileSidebarCardLabel,
  reconcileActionButtonOrder,
  reconcileSidebarOrder,
  usesSpecificAllthenticateProducts
} from '../../../../../../utils-models';
import {
  CustomerProfileActionButton,
  CustomerProfileSidebarCard,
  ProfileField
} from '../../../../../../models/organization-config-enums';
import { ComponentHasUnsavedChanges } from '../../../../../../unsaved-changes-guard.service';
import { CustomerProfilePreviewModalComponent } from '../modals/customer-profile-preview-modal/customer-profile-preview-modal.component';


/**
 * The component allowing a user to edit their organization's customer profile
 * layout.
 */
@Component({
  selector: 'app-customer-profile-layout',
  templateUrl: './customer-profile-layout.component.html',
  styleUrls: ['./customer-profile-layout.component.css']
})
export class CustomerProfileLayoutComponent implements OnInit, ComponentHasUnsavedChanges {

  public organizationConfig: OrganizationConfig;
  public customCustomerFields: CustomCustomerField[] = null;

  public loadingOrganizationConfig = false;
  public savingOrganizationConfig = false;

  public unsavedChanges = false;

  public successMessage = '';
  public errors: any = {};

  public serverError: number = null;

  public ProfileField = ProfileField;
  public CustomerProfileSidebarCard = CustomerProfileSidebarCard;
  public getCustomerProfileSidebarCardLabel = getCustomerProfileSidebarCardLabel;
  public CustomerProfileActionButton = CustomerProfileActionButton;
  public getCustomerProfileActionButtonLabel = getCustomerProfileActionButtonLabel;

  public sidebarOrder: CustomerProfileSidebarCard[] = [];
  public actionButtonOrder: CustomerProfileActionButton[] = [];

  public currentSectionId = 'profile-grid';
  public readonly scrollToElement = scrollToElement;

  public CONFIG_OPTIONS = [
    ProfileField.name,
    ProfileField.preferred_name,
    ProfileField.email,
    ProfileField.emailIsOptedIn,
    ProfileField.telephone,
    ProfileField.age_birthday,
    ProfileField.street_address,
    ProfileField.city,
    ProfileField.state,
    ProfileField.zipcode,
    ProfileField.country,
    ProfileField.gym_status,
    ProfileField.membership_status,
    ProfileField.punchpass_status,
    ProfileField.guest_passes,
    ProfileField.guest_passes_all_customers,
    ProfileField.capitan_status,
    ProfileField.id_number,
    ProfileField.last_check_in,
    ProfileField.check_in_count,
    ProfileField.gender,
    ProfileField.pronouns,
    ProfileField.barcode,
    ProfileField.homeLocation,
    ProfileField.referralCode,
  ];

  constructor(
    private modalService: NgbModal,
    private organizationConfigService: OrganizationConfigService
  ) { }

  ngOnInit() {
    this.loadOrganizationConfig();
    this.loadCustomCustomerFields();
  }

  hasUnsavedChanges() {
    return this.unsavedChanges;
  }

  @HostListener('window:beforeunload')
  allowUnload() {
    return !this.hasUnsavedChanges();
  }

  /**
   * Load the organization config from the API.
   *
   * We do not use the local-stored copy, in order to avoid pop-in of changed
   * values.
   */
  public loadOrganizationConfig() {
    this.loadingOrganizationConfig = true;

    this.organizationConfigService
      .loadOrganizationConfig()
      .then(() => {
        this.organizationConfig = this.organizationConfigService
          .getOrganizationConfig();
        this.setSidebarOrder();
        this.actionButtonOrder = reconcileActionButtonOrder(
          this.organizationConfig.customer_profile_action_button_order
        );
        this.loadingOrganizationConfig = false;
        this.unsavedChanges = false;
      });
  }

  /**
   * Save the updated organization config to the API.
   */
  public saveOrganizationConfig() {
    this.savingOrganizationConfig = true;
    this.successMessage = '';

    this.organizationConfig.customer_profile_sidebar_order = this.sidebarOrder;
    this.organizationConfig.customer_profile_action_button_order = this.actionButtonOrder;

    this.organizationConfigService
      .updateCustomerProfileConfig(this.organizationConfig)
      .then(updatedOrgConf => {
        this.organizationConfig = updatedOrgConf;
        this.setSidebarOrder();
        this.actionButtonOrder = reconcileActionButtonOrder(
          this.organizationConfig.customer_profile_action_button_order
        );
        this.successMessage = 'Customer profile updated successfully.';
        this.unsavedChanges = false;
      })
      .catch(reason => {
        if (reason.status === 400) {
          this.errors = reason.error;
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.savingOrganizationConfig = false; });
  }

  public openPreview() {
    this.organizationConfig.customer_profile_sidebar_order = this.sidebarOrder;
    this.organizationConfig.customer_profile_action_button_order = this.actionButtonOrder;
    const modalRef = this.modalService.open(
      CustomerProfilePreviewModalComponent,
      { size: 'xl' }
    );
    modalRef.componentInstance.organizationConfig = this.organizationConfig;
    modalRef.componentInstance.customCustomerFields = this.customCustomerFields;
  }

  /**
   * Reconcile the stored sidebar order, then drop cards this org has no use
   * for.
   *
   * The filter is applied to the array rather than in the template because
   * dropSidebarCard reorders by displayed position - a hidden entry would
   * shift every index past it. A card filtered out here is simply absent from
   * what gets saved, and reconcileSidebarOrder appends it again if the org
   * later starts using it.
   */
  private setSidebarOrder() {
    const order = reconcileSidebarOrder(
      this.organizationConfig.customer_profile_sidebar_order
    );

    if (this.usesSpecificAllthenticateProducts()) {
      this.sidebarOrder = order;
      return;
    }

    this.sidebarOrder = order.filter(
      card => card !== CustomerProfileSidebarCard.allthenticateStatus
    );
  }

  public usesSpecificAllthenticateProducts(): boolean {
    return usesSpecificAllthenticateProducts(this.organizationConfig);
  }

  public dropSidebarCard(event: CdkDragDrop<CustomerProfileSidebarCard[]>) {
    moveItemInArray(this.sidebarOrder, event.previousIndex, event.currentIndex);
    this.unsavedChanges = true;
  }

  public dropActionButton(event: CdkDragDrop<CustomerProfileActionButton[]>) {
    moveItemInArray(this.actionButtonOrder, event.previousIndex, event.currentIndex);
    this.unsavedChanges = true;
  }

  public onSectionChange(sectionId: string) {
    this.currentSectionId = sectionId;
  }

  public getThisCustomerFieldLabel(profileField: ProfileField) {
    return getCustomerFieldLabel(
      profileField,
      this.customCustomerFields
    );
  }

  private loadCustomCustomerFields() {
    this.organizationConfigService
      .getCustomCustomerFields(1, 3000)
      .then(response => {
        this.customCustomerFields = response.results;
      });
  }

}
